// CoachComposer — textarea + Send + optional dictation.
//
// Per designer handoff: single-row (auto-growing) textarea with a Send
// button on the right. Below, a mono context line: "Enter to send ·
// Shift+Enter for a new line" on the left, "◈ Using your notebook" on
// the right (hidden until memory lands in MR D).
//
// Dictation V1 (behind env.dictationEnabled): a mic button sits in the
// composer right-side group, next to Send. When live/paused, the composer
// renders a live meter + visible partial-text indicator; Send is disabled
// (tooltip "Stop dictation to send"). The full "Microphone on" strip and
// viewport ring live in a sibling <DictationStrip> rendered next to the
// composer in the parent component. See
// design/UI : UX v1.0/for-designer-consumer-dictation-v1/ for the full
// walkthrough.

import { useEffect, type FormEvent, type KeyboardEvent, useRef } from 'react';
import { useI18n } from '../i18n/index.js';
import styles from './Coach.module.css';
import type { DictationApi } from './dictation/useDictation.js';

export interface CoachComposerProps {
  value: string;
  onChange: (next: string) => void;
  onSubmit: () => void;
  disabled?: boolean;
  /** Custom placeholder (empty-state uses a different line). */
  placeholder?: string;
  /** When true, shows the "Using your notebook" marker (MR D wires this). */
  memoryActive?: boolean;
  /** Optional dictation API. When provided, the composer renders the mic
   *  button and partial-text overlay. The parent is expected to render
   *  <DictationStrip> as a sibling so the viewport ring + top strip appear
   *  in lockstep. */
  dictation?: DictationApi;
}

export function CoachComposer({
  value,
  onChange,
  onSubmit,
  disabled,
  placeholder,
  memoryActive = false,
  dictation,
}: CoachComposerProps) {
  const { t } = useI18n();
  const taRef = useRef<HTMLTextAreaElement>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const freshMarkRef = useRef<number>(0);

  // Scroll the composer (and the pre-permission popover above it) into view
  // when the explainer first opens. Without this, if the chat body is tall,
  // "Allow microphone" renders below the fold and users miss it — Olivier
  // 2026-11-01: "I click the mic and nothing seems to happen" was actually
  // the explainer appearing out of view.
  useEffect(() => {
    if (!dictation) return;
    if (dictation.state !== 'permission') return;
    formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [dictation, dictation?.state]);

  // Append dictation final text to the composer value as finals arrive.
  // The hook exposes the full accumulated finalText; we track the previous
  // length so we only append the delta. A new session resets finalText to
  // '', which the length tracker also handles cleanly.
  const prevFinalLenRef = useRef(0);
  useEffect(() => {
    if (!dictation) return;
    const next = dictation.finalText;
    const prevLen = prevFinalLenRef.current;
    if (next.length < prevLen) {
      // Session reset (discard, new session). Just update the tracker.
      prevFinalLenRef.current = next.length;
      return;
    }
    const delta = next.slice(prevLen);
    prevFinalLenRef.current = next.length;
    if (!delta) return;
    // Space-separate from existing composer content when needed.
    const sep = value && !value.endsWith(' ') && !delta.startsWith(' ') ? ' ' : '';
    onChange(value + sep + delta);
    freshMarkRef.current = Date.now();
  }, [dictation, dictation?.finalText, onChange, value]);

  // Keyboard shortcut — ⌘⇧M / Ctrl+Shift+M starts dictation; Esc ends it.
  // Mounted globally so the shortcut works from anywhere in the Coach view;
  // handlers no-op when dictation isn't enabled.
  useEffect(() => {
    if (!dictation) return;
    const handler = (e: KeyboardEvent | globalThis.KeyboardEvent) => {
      if (e.key === 'm' || e.key === 'M') {
        if ((e.metaKey || e.ctrlKey) && e.shiftKey) {
          e.preventDefault();
          if (dictation.state === 'idle') dictation.requestStart();
          else if (dictation.state === 'live' || dictation.state === 'ending-soon')
            dictation.stop();
        }
      } else if (e.key === 'Escape') {
        if (
          dictation.state === 'live' ||
          dictation.state === 'paused' ||
          dictation.state === 'ending-soon'
        ) {
          dictation.stop();
        }
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [dictation]);

  const dictActive =
    dictation?.state === 'live' ||
    dictation?.state === 'paused' ||
    dictation?.state === 'ending-soon';
  const sendDisabled = !!disabled || !value.trim() || !!dictActive;

  function handleKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (value.trim() && !sendDisabled) onSubmit();
    }
  }

  function handleFormSubmit(e: FormEvent) {
    e.preventDefault();
    if (value.trim() && !sendDisabled) onSubmit();
  }

  const compbClass = [
    styles.compb,
    dictation?.state === 'live' || dictation?.state === 'ending-soon' ? styles.compbLive : '',
    dictation?.state === 'paused' ? styles.compbPaused : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <form className={styles.comp} onSubmit={handleFormSubmit} ref={formRef}>
      {dictation && <DictationPopover d={dictation} />}
      <div className={compbClass}>
        <textarea
          ref={taRef}
          rows={1}
          className={styles.compTextarea}
          placeholder={placeholder ?? t('coach.composer.placeholder')}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={disabled}
        />
        {dictation && <MicGroup d={dictation} />}
        <button
          type="submit"
          className={`${styles.btn} ${styles.btnBrand}`}
          disabled={sendDisabled}
          title={dictActive ? t('coach.dictation.sendDisabled') : undefined}
        >
          {t('coach.send')}
        </button>
      </div>
      {dictation && dictation.partialText && (
        <div className={styles.ctxl}>
          <span className={styles.dictPartial}>{dictation.partialText}</span>
        </div>
      )}
      <div className={styles.ctxl}>
        <span>{t('coach.composer.hint')}</span>
        {memoryActive && (
          <span className={styles.ctxlMemory}>◈ {t('coach.composer.memory')}</span>
        )}
      </div>
    </form>
  );
}

// Inline SVG icon for the mic button.
function MicIcon({ struck = false }: { struck?: boolean }) {
  return (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <rect x="5" y="1.5" width="6" height="9" rx="3" stroke="currentColor" strokeWidth="1.5" />
      <path
        d="M3 7.5a5 5 0 0 0 10 0M8 12.5V15"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
      {struck && (
        <path d="M2 2l12 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      )}
    </svg>
  );
}

function PauseIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 12 12" aria-hidden="true">
      <rect x="2" y="1.5" width="2.6" height="9" fill="currentColor" />
      <rect x="7.4" y="1.5" width="2.6" height="9" fill="currentColor" />
    </svg>
  );
}

function MicGroup({ d }: { d: DictationApi }) {
  const { t } = useI18n();

  if (d.state === 'live' || d.state === 'ending-soon') {
    return (
      <>
        <span className={styles.dictMeter} aria-hidden="true">
          <i /><i /><i /><i />
        </span>
        <button
          type="button"
          className={`${styles.dictMic} ${styles.dictMicLive}`}
          aria-pressed="true"
          aria-label={t('coach.dictation.button.live')}
          title={t('coach.dictation.button.live')}
          onClick={d.stop}
        >
          <MicIcon />
        </button>
      </>
    );
  }

  if (d.state === 'paused') {
    return (
      <>
        <span style={{ fontFamily: 'var(--font-mono)', fontSize: '9px', color: 'var(--warn)' }}>
          {t('coach.dictation.label.paused')}
        </span>
        <button
          type="button"
          className={`${styles.dictMic} ${styles.dictMicPaused}`}
          aria-pressed="false"
          aria-label={t('coach.dictation.button.paused')}
          title={t('coach.dictation.button.paused')}
          onClick={() => void d.resume()}
        >
          <PauseIcon />
        </button>
      </>
    );
  }

  if (d.state === 'denied') {
    return (
      <button
        type="button"
        className={`${styles.dictMic} ${styles.dictMicDenied}`}
        aria-label={t('coach.dictation.button.denied')}
        onClick={() => {
          /* clicking the denied mic re-opens the denied popover via hook state */
        }}
      >
        <MicIcon struck />
      </button>
    );
  }

  return (
    <button
      type="button"
      className={styles.dictMic}
      aria-label={t('coach.dictation.button.idle')}
      title={t('coach.dictation.button.idleTooltip')}
      onClick={d.requestStart}
    >
      <MicIcon />
    </button>
  );
}

function DictationPopover({ d }: { d: DictationApi }) {
  const { t } = useI18n();

  if (d.state === 'permission') {
    return (
      <div className={styles.dictPop}>
        <h4>{t('coach.dictation.prepermission.title')}</h4>
        <p>{t('coach.dictation.prepermission.body')}</p>
        <p style={{ fontSize: '12.5px', color: 'var(--ink3)' }}>
          {t('coach.dictation.prepermission.check')}
        </p>
        <div className={styles.dictPopActs}>
          <button
            type="button"
            className={`${styles.dictPopBtn} ${styles.dictPopBtnPri}`}
            onClick={() => void d.grantAndStart()}
          >
            {t('coach.dictation.prepermission.allow')}
          </button>
          <button type="button" className={styles.dictPopBtn} onClick={d.cancelStart}>
            {t('coach.dictation.prepermission.later')}
          </button>
        </div>
      </div>
    );
  }

  if (d.state === 'denied') {
    return (
      <div className={`${styles.dictPop} ${styles.dictPopErr}`}>
        <h4>{t('coach.dictation.denied.title')}</h4>
        <p>{t('coach.dictation.denied.body')}</p>
        <ol style={{ margin: '4px 0 10px', paddingLeft: '18px', fontSize: '12.5px' }}>
          <li>{t('coach.dictation.denied.step1')}</li>
          <li>{t('coach.dictation.denied.step2')}</li>
        </ol>
        <div className={styles.dictPopActs}>
          <button type="button" className={styles.dictPopBtn} onClick={d.cancelStart}>
            {t('coach.dictation.keepTyping')}
          </button>
        </div>
      </div>
    );
  }

  if (d.state === 'error') {
    return (
      <div className={`${styles.dictPop} ${styles.dictPopErr}`}>
        <h4>{t('coach.dictation.error.title')}</h4>
        <p>{t('coach.dictation.error.body')}</p>
        <div className={styles.dictPopActs}>
          <button
            type="button"
            className={`${styles.dictPopBtn} ${styles.dictPopBtnPri}`}
            onClick={() => void d.grantAndStart()}
          >
            {t('coach.dictation.error.retry')}
          </button>
          <button type="button" className={styles.dictPopBtn} onClick={d.cancelStart}>
            {t('coach.dictation.keepTyping')}
          </button>
        </div>
      </div>
    );
  }

  return null;
}
